import React, { useContext, useEffect } from "react";
import { omit } from "lodash";
import queryString from "query-string";
import { useHistory, useLocation } from "react-router";
import { buildLink } from "common/routing";
import {
  MapsForAccountQuery,
  useMapsForAccountQuery,
} from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { GuestMap } from "../Guest/Explore";
import { InternalMapContextProvider } from "./InternalMapContextProvider";
import { LayerContextProvider, LayerState } from "./layers";
import { InternalMap } from "./Map";

export default () => {
  const { loading, error, data } = useMapsForAccountQuery({
    variables: {},
    fetchPolicy: "network-only",
  });

  if (loading || error || !data?.account?.bounds) {
    return <div />;
  }

  return <MapWithState account={data.account} />;
};

const MapDisplay = ({
  account,
  isPublic,
}: {
  account: NonNullable<MapsForAccountQuery["account"]>;
  isPublic: boolean;
}) =>
  isPublic ?
    <GuestMap account={account} />
  : <InternalMapContextProvider>
      <InternalMap account={account} />
    </InternalMapContextProvider>;

const MapWithState = ({
  account,
}: {
  account: NonNullable<MapsForAccountQuery["account"]>;
}) => {
  const { isPublic } = useContext(AuthContext);
  const defaultState = useDefaultState();

  return (
    <LayerContextProvider account={account} defaultState={defaultState}>
      <MapDisplay account={account} isPublic={isPublic} />
    </LayerContextProvider>
  );
};

const useDefaultState = () => {
  const { account, isPublic } = useContext(AuthContext);
  const location =
    useLocation<Maybe<{ savedViewId?: string; prevLocation?: string }>>();
  const history = useHistory();

  const queryParams = queryString.parse(location.search);
  const savedViewId =
    location.state?.savedViewId ||
    (queryParams.savedViewId as string | undefined);

  const defaultDocumentState: Record<string, boolean> =
    isPublic ?
      {}
    : (account?.accountDocumentTypes.reduce(
        (acc, type, index) => {
          acc[type.id] = savedViewId ? false : index === 0;
          return acc;
        },
        {} as Record<string, boolean>,
      ) ?? {});

  const defaultState: Partial<LayerState> = {
    documents: defaultDocumentState,
    ...(savedViewId && { savedViews: { [savedViewId]: true } }),
  };
  // useEffect runs on a different thread/tick than the initial render, so we can confidently
  // grab a value from location.state before it gets removed in the useEffect
  useEffect(() => {
    const stateSavedViewId = location.state?.savedViewId;
    if (stateSavedViewId) {
      const newState = omit(location.state, "savedViewId");
      if (isPublic) {
        newState.prevLocation = buildLink("residentSavedView", {
          savedViewId: stateSavedViewId,
        });
      }
      history.replace({ state: newState });
    }
    // Clean up query param if it exists (for new tab case)
    const currentQueryParams = queryString.parse(location.search);
    if (currentQueryParams.savedViewId && !stateSavedViewId) {
      const newQueryParams = { ...currentQueryParams };
      delete newQueryParams.savedViewId;
      const newSearch = queryString.stringify(newQueryParams);
      history.replace({
        pathname: location.pathname,
        search: newSearch || undefined,
        state: location.state,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return defaultState;
};
